import { Link } from "react-router-dom";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Calendar, Clock, Info } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { INFO_SATZ, INFO_TYP, infoAdresse, type KommendeInfo } from "@/lib/termine/infoveranstaltungen";
import { INFO_KNOPF } from "@/lib/termine/infoLeiste";

// Infoveranstaltungen auf der heutigen Seite /termine (Sitzung 14b, Daniel 22.10.2026: „ganz normal wie einen
// Termin“), im Aussehen der Karten dort: Liste nach Monat, Raster und Kalender. Ein Klick führt zur Anmeldung auf der
// Landingpage `/info/<adresse>`; kostenlos, ohne Konto. Die neue Seite nimmt `InfoKarte` aus
// src/components/webseite/termine/Teile.tsx.

const GRUEN = "bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 hover:bg-green-100 border-none";

export function InfoListenKarte({ info, countdown }: { info: KommendeInfo; countdown: string | null }) {
  const zu = infoAdresse(info, "termine");
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-all duration-300 border-l-4 border-l-primary hover:scale-[1.02]">
      <div className="flex flex-col sm:flex-row">
        {info.bild_url && (
          <Link to={zu} className="sm:w-64 sm:flex-shrink-0 overflow-hidden" tabIndex={-1} aria-hidden="true">
            <AspectRatio ratio={16 / 9} className="bg-muted sm:h-full">
              <img src={info.bild_url} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </AspectRatio>
          </Link>
        )}
        <div className="flex-1 p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-center flex-wrap gap-2">
              <Badge variant="secondary" className="text-xs">
                {INFO_TYP}
              </Badge>
              <Badge className={GRUEN}>Kostenlos</Badge>
              {countdown && (
                <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20">
                  {countdown}
                </Badge>
              )}
            </div>
            <h3 className="font-bold text-lg leading-tight">
              <Link to={zu} className="hover:underline">
                {info.titel}
              </Link>
            </h3>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span className="font-medium">{format(new Date(info.beginn), "EEEE, d. MMMM", { locale: de })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span className="font-medium">{format(new Date(info.beginn), "HH:mm", { locale: de })} Uhr</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">{info.untertitel || INFO_SATZ}</p>
          </div>
          <div className="lg:flex-shrink-0 flex flex-col sm:flex-row lg:flex-col gap-2 lg:min-w-[140px]">
            <Button asChild size="sm" variant="outline" className="flex-1 lg:flex-none">
              <Link to={zu}>
                <Info className="h-4 w-4 mr-2" />
                Mehr Infos
              </Link>
            </Button>
            <Button asChild size="sm" className="flex-1 lg:flex-none">
              <Link to={zu}>{INFO_KNOPF}</Link>
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

export function InfoRasterKarte({ info, countdown }: { info: KommendeInfo; countdown: string | null }) {
  const zu = infoAdresse(info, "termine");
  return (
    <Card className="flex flex-col overflow-hidden hover:shadow-lg transition-all duration-300 hover:scale-105 group">
      <Link to={zu} className="flex flex-col flex-1">
        {info.bild_url && (
          <AspectRatio ratio={4 / 3} className="bg-muted overflow-hidden">
            <img src={info.bild_url} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
          </AspectRatio>
        )}
        <CardHeader className="p-4">
          <div className="flex items-start justify-between mb-2 gap-2">
            <Badge variant="secondary" className="text-xs">
              {INFO_TYP}
            </Badge>
            {countdown && (
              <Badge variant="outline" className="text-xs bg-accent/10 text-accent border-accent/20">
                {countdown}
              </Badge>
            )}
          </div>
          <CardTitle className="text-base leading-tight">{info.titel}</CardTitle>
          {info.untertitel && <CardDescription className="line-clamp-1 text-xs">{info.untertitel}</CardDescription>}
        </CardHeader>
        <CardContent className="flex-1 flex flex-col p-4 pt-0">
          <div className="space-y-2 mb-3">
            <div className="flex items-center gap-2 text-xs">
              <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{format(new Date(info.beginn), "d. MMM yyyy", { locale: de })}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
              <span>{format(new Date(info.beginn), "HH:mm", { locale: de })} Uhr</span>
            </div>
          </div>
          <Badge className={`${GRUEN} mb-3 w-fit`}>Kostenlos</Badge>
        </CardContent>
      </Link>
    </Card>
  );
}

export function InfoKalenderEintrag({ info }: { info: KommendeInfo }) {
  return (
    <Link
      to={infoAdresse(info, "termine")}
      className="block w-full text-left p-1.5 rounded text-xs bg-primary/10 hover:bg-primary/20 transition-colors border-l-2 border-primary"
    >
      <div className="font-medium line-clamp-1">{info.titel}</div>
      <div className="text-muted-foreground flex items-center gap-1 mt-0.5">
        <Clock className="h-3 w-3" />
        {format(new Date(info.beginn), "HH:mm", { locale: de })}
      </div>
      <Badge className="mt-1 text-[10px] h-4 px-1 bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 border-none">
        {INFO_TYP}
      </Badge>
    </Link>
  );
}
